// src/hooks/tokens/compare/useCompareData.ts

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { 
  fetchTokenCategoryMap, 
  fetchTokensChanges, 
  fetchTokensRollups,
  fetchTokensHistoryPoints,
  fetchTokensHistoryRatios,
} from '@/services/queries/tokens/tokensScreenerQueries';
import { TIME_WINDOWS, MetricKeys } from '@/constants/metrics';

/**
 * Hook to fetch token category mapping
 */
export function useTokenCategoryMap() {
  return useQuery({
    queryKey: ['tokens', 'category-map'],
    queryFn: ({ signal }) => fetchTokenCategoryMap({ signal }),
    staleTime: 1000 * 60 * 60,
  });
}

/**
 * Hook to fetch token changes (percentage changes over time windows)
 */
export function useTokensChanges() {
  const windowsCsv = useMemo(() => TIME_WINDOWS.API.join(','), []);
  
  const query = useQuery({
    queryKey: ['tokens', 'compare', 'changes', windowsCsv],
    queryFn: ({ signal }) => fetchTokensChanges({ windowsCsv, sortOrder: 'desc', limit: 1000, signal }),
    staleTime: 60_000,
  });

  const changeByTicker = useMemo(() => {
    const map = new Map<string, Record<string, number | null>>();
    const tokens = query.data?.tokens || [];
    for (const t of tokens) {
      const rec: Record<string, number | null> = {};
      for (const m of t.metrics || []) {
        const windows = m.windows || {};
        for (const [win, val] of Object.entries(windows)) {
          const v = (val as { value?: number | null } | undefined)?.value ?? null;
          rec[`${m.metric_key}_${win}`] = v == null ? null : (Number(v) * 100);
        }
      }
      map.set(t.ticker, rec);
    }
    return map;
  }, [query.data]);

  return { query, changeByTicker };
}

/**
 * Hook to fetch token rollups (aggregated values over time windows)
 */
export function useTokensRollups() {
  const windowsCsv = useMemo(() => TIME_WINDOWS.API.join(','), []);
  
  const query = useQuery({
    queryKey: ['tokens', 'compare', 'rollups', windowsCsv],
    queryFn: ({ signal }) => fetchTokensRollups({ windowsCsv, sortOrder: 'desc', limit: 1000, signal }),
    staleTime: 60_000,
  });

  const rollupByTicker = useMemo(() => {
    const map = new Map<string, Record<string, number | null>>();
    const tokens = query.data?.tokens || [];
    for (const t of tokens) {
      const rec: Record<string, number | null> = {};
      for (const m of t.metrics || []) {
        const windows = m.windows || {};
        for (const [win, val] of Object.entries(windows)) {
          const v = (val as { value?: number | null } | undefined)?.value ?? null;
          rec[`${m.metric_key}_${win}`] = v;
        }
      }
      map.set(t.ticker, rec);
    }
    return map;
  }, [query.data]);

  return { query, rollupByTicker };
}

/**
 * Hook to fetch historical time series data for tokens
 */
export function useTokensHistory(
  tickers: string[],
  metricKey: string,
  enabled: boolean = true
) {
  const isComputedMetric = metricKey.startsWith('computed:');
  const isRatioMetric = metricKey.startsWith('ratio:');
  
  // For computed metrics, fetch price data
  const fetchMetricKey = isComputedMetric ? MetricKeys.PriceClose : metricKey;
  
  // Include BTC for comparative analysis
  const allTickers = useMemo(() => {
    const tickerList = [...tickers];
    if (!tickerList.includes('BTC')) tickerList.push('BTC');
    return tickerList.join(',');
  }, [tickers]);

  return useQuery({
    queryKey: ['tokens', 'history', isRatioMetric ? 'ratios' : 'points', 'compare', allTickers, fetchMetricKey],
    queryFn: ({ signal }) => {
      if (isRatioMetric) {
        return fetchTokensHistoryRatios({ tickersCsv: allTickers, ratioKeysCsv: fetchMetricKey, signal });
      } else {
        return fetchTokensHistoryPoints({ tickersCsv: allTickers, metricKeysCsv: fetchMetricKey, signal });
      }
    },
    enabled: enabled && Boolean(allTickers && fetchMetricKey),
    staleTime: 60_000,
  });
}

